import { useEffect } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { parseCvAnalyticsCv } from '../../lib/cv-analytics-view';
import { useCvAnalytics, useCvAnalyticsScopeState } from './useCvAnalytics';
import {
  CvAnalyticsStyles,
  CvaChart,
  CvaError,
  CvaFullButton,
  CvaHeader,
  CvaLiveStatus,
  CvaLoading,
  CvaMetrics,
  CvaScope,
  CvaStack,
} from './cv-analytics-web.web';

interface CvAnalyticsSheetProps {
  visible: boolean;
  onClose: () => void;
  target?: string | null;
}

/**
 * Quick look from the CV bottom bar's Analytics button (4 Oct 2026 handoff,
 * "Analytics · quick look"). Totals, chart and the latest activity for the
 * CV in view; Free sees the counts and one activity, with the rest of the
 * feed behind Explore Premium. "View full analytics" carries the CV, duration
 * and metric into the full screen. No X: Escape or the backdrop dismisses it.
 */
export function CvAnalyticsSheet({ visible, onClose, target }: CvAnalyticsSheetProps) {
  const router = useRouter();
  const { t, i18n } = useTranslation('seeker');
  const { scope, setScope, update } = useCvAnalyticsScopeState({
    cvId: parseCvAnalyticsCv(target),
  });
  const model = useCvAnalytics(scope, {
    enabled: visible,
    entryPoint: 'cv_analytics_sheet.activity',
  });
  const direction = typeof i18n.dir === 'function' ? i18n.dir(i18n.language) : 'ltr';

  // Every opening starts from the CV in view and all time.
  useEffect(() => {
    if (visible) setScope({ range: 'all', cvId: parseCvAnalyticsCv(target), metric: 'views' });
  }, [setScope, target, visible]);

  // Leave the dialog first, then move: a route push under an open dialog
  // leaves its overlay on top of the next screen.
  const openFull = () => {
    onClose();
    window.requestAnimationFrame(() => {
      router.push({
        pathname: '/(app)/analytics',
        params: {
          range: scope.range,
          metric: scope.metric,
          cv: scope.cvId ?? 'all',
          // The full screen is a drawer screen that stays mounted: a fresh
          // value makes it take this scope even when the rest repeats.
          at: String(Date.now()),
        },
      } as never);
    });
  };

  const upgrade = () => {
    onClose();
    model.openUpgrade('cv_analytics_sheet.activity');
  };

  return (
    <>
      {visible ? <CvAnalyticsStyles /> : null}
      <WebDialog
        visible={visible}
        title={t('cvAnalytics.hub.title')}
        onClose={onClose}
        maxWidth={430}
        hideHeader
      >
        <div
          className="cva cva-popout"
          dir={direction}
          style={{
            // The dialog leaves its chrome and scrolling to a hideHeader child.
            minHeight: 0,
            overflowY: 'auto',
            padding: '18px 24px 24px',
            ['--cva-surface' as string]: 'var(--menu-bg)',
          }}
        >
          <CvaHeader title={t('cvAnalytics.hub.title')} />
          <CvaScope model={model} scope={scope} onScopeChange={update} />
          {model.isLoading ? (
            <CvaLoading />
          ) : model.error ? (
            <CvaError onRetry={() => void model.query.refetch()} />
          ) : (
            <>
              <CvaMetrics model={model} metric={scope.metric} onChange={(metric) => update({ metric })} />
              <CvaChart model={model} metric={scope.metric} compact />
              <CvaStack model={model} onViewFull={openFull} onUpgrade={upgrade} />
            </>
          )}
          <CvaFullButton label={t('cvAnalytics.hub.viewFullAnalytics')} onClick={openFull} />
          <CvaLiveStatus model={model} scope={scope} />
        </div>
      </WebDialog>
    </>
  );
}
